/* eslint-disable react/prop-types */

/* src/components/GoogleMaps.jsx */


import React, { useMemo, useState, useRef, useEffect } from "react";
import { GoogleMap } from "@react-google-maps/api";
import useGoogleMaps from "~/provider/useGoogleMaps";
import PlantsIcon from "~/assets/images/growing.png";

const containerStyle = {
  width: "100%",
  height: "500px",
};

const createSvgIcon = (color) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48">
    <circle cx="24" cy="24" r="22" fill="${color}" />
    <text x="24" y="30" font-size="16" text-anchor="middle" fill="white">🌱</text>
  </svg>
`;

function createMarker(position, iconUrl, map) {
  return new window.google.maps.Marker({
    position,
    map,
    icon: {
      url: iconUrl,
      scaledSize: new window.google.maps.Size(40, 40),
      anchor: new window.google.maps.Point(20, 20),
    },
  });
}

export default function GoogleMapsNew({ coordinatesArray }) {
  const { isLoaded } = useGoogleMaps();

  const markersRef = useRef([]);
  const infoRef = useRef(null);

  const [mapInstance, setMapInstance] = useState(null);

  // Layer visibility states
  const [plotVisible, setPlotVisible] = useState(false);
  const [plantationVisible, setPlantationVisible] = useState({});
  const [detailVisible, setDetailVisible] = useState({});

  // Center map
  const center = useMemo(() => {
    if (!coordinatesArray || !coordinatesArray.length) return { lat: 0, lng: 0 };
    const last = coordinatesArray[coordinatesArray.length - 1];
    return { lat: Number(last.Latitude), lng: Number(last.Longitude) };
  }, [coordinatesArray]);

  const clearMarkers = () => {
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
  };

  const buildInfoHTML = (node) => {
    if (node?.plant_mstr) {
      return `
        <h3>${node.plant_mstr.Plant_Local_Name}</h3>
        <p>Latitude: ${node.Latitude}</p>
        <p>Longitude: ${node.Longitude}</p>
        <p>Total Monitoring Plants: ${
          node.plantTationCount || node.plantationdtl?.length || "-"
        }</p>
      `;
    }

    return `
      <h3>${node.BarCode_Serial_No || node.Location}</h3>
      <p>Latitude: ${node.Latitude}</p>
      <p>Longitude: ${node.Longitude}</p>
      <p>Total: ${node.plantTationCount || "-"}</p>
    `;
  };

  // Hover InfoWindow functions
  const showHoverInfo = (marker, html) => {
    infoRef.current.setContent(html);
    infoRef.current.open({
      anchor: marker,
      map: mapInstance,
      pixelOffset: new window.google.maps.Size(0, -40),
    });

    setTimeout(() => {
      const iw = document.querySelector(".gm-style-iw");
      if (iw) iw.classList.add("gm-info-visible");
    }, 20);
  };

  const closeHoverInfo = () => {
    infoRef.current.close();
  };

  // Main render logic
  useEffect(() => {
    if (!isLoaded || !mapInstance) return;

    clearMarkers();

    const google = window.google;

    if (!infoRef.current) {
      infoRef.current = new google.maps.InfoWindow();
    }

    const main = coordinatesArray[coordinatesArray.length - 1];
    const mainPos = { lat: Number(main.Latitude), lng: Number(main.Longitude) };

    // MAIN MARKER
    const mainMarker = createMarker(
      mainPos,
      `data:image/svg+xml,${encodeURIComponent(
        createSvgIcon(main.plant_mstr?.Plant_Color || "green")
      )}`,
      mapInstance
    );

    // main marker listeners
    mainMarker.addListener("click", () => {
      setPlotVisible((prev) => !prev);
      showHoverInfo(mainMarker, buildInfoHTML(main));
    });

    mainMarker.addListener("mouseover", () => {
      showHoverInfo(mainMarker, buildInfoHTML(main));
    });

    mainMarker.addListener("mouseout", closeHoverInfo);

    markersRef.current.push(mainMarker);

    // LEVEL 1 — PLOT MARKERS
    coordinatesArray.slice(0, -1).forEach((plot, plotIndex) => {
      if (!plotVisible) return;

      const plotPos = {
        lat: Number(plot.Latitude),
        lng: Number(plot.Longitude),
      };

      const plotIcon =
        plot.value === "plot"
          ? "https://maps.google.com/mapfiles/ms/icons/blue-dot.png"
          : `data:image/svg+xml,${encodeURIComponent(
              createSvgIcon(plot.plant_mstr?.Plant_Color || "green")
            )}`;

      const plotMarker = createMarker(plotPos, plotIcon, mapInstance);

      plotMarker.addListener("click", () => {
        showHoverInfo(plotMarker, buildInfoHTML(plot));

        setPlantationVisible((prev) => ({
          ...prev,
          [plotIndex]: !prev[plotIndex],
        }));
      });

      plotMarker.addListener("mouseover", () => {
        showHoverInfo(plotMarker, buildInfoHTML(plot));
      });

      plotMarker.addListener("mouseout", closeHoverInfo);

      markersRef.current.push(plotMarker);

      // LEVEL 2 — PLANTATION MARKERS
      if (plantationVisible[plotIndex] && plot.plantationLocations) {
        plot.plantationLocations.forEach((plant, plantIndex) => {
          const plantPos = {
            lat: Number(plant.Latitude),
            lng: Number(plant.Longitude),
          };

          const plantMarker = createMarker(
            plantPos,
            `data:image/svg+xml,${encodeURIComponent(
              createSvgIcon(plant.plant_mstr?.Plant_Color || "green")
            )}`,
            mapInstance
          );

          plantMarker.addListener("click", () => {
            showHoverInfo(plantMarker, buildInfoHTML(plant));

            setDetailVisible((prev) => ({
              ...prev,
              [`${plotIndex}-${plantIndex}`]: !prev[`${plotIndex}-${plantIndex}`],
            }));
          });

          plantMarker.addListener("mouseover", () => {
            showHoverInfo(plantMarker, buildInfoHTML(plant));
          });

          plantMarker.addListener("mouseout", closeHoverInfo);

          markersRef.current.push(plantMarker);

          // LEVEL 3 — DETAILED PLANT MARKERS
          if (
            detailVisible[`${plotIndex}-${plantIndex}`] &&
            plant.plantationdtl
          ) {
            plant.plantationdtl.forEach((detail) => {
              if (!detail.Latitude || !detail.Longitude) return;

              const dPos = {
                lat: Number(detail.Latitude),
                lng: Number(detail.Longitude),
              };

              const detailMarker = createMarker(dPos, PlantsIcon, mapInstance);

              detailMarker.addListener("click", () =>
                showHoverInfo(detailMarker, buildInfoHTML(detail))
              );

              detailMarker.addListener("mouseover", () =>
                showHoverInfo(detailMarker, buildInfoHTML(detail))
              );

              detailMarker.addListener("mouseout", closeHoverInfo);

              markersRef.current.push(detailMarker);
            });
          }
        });
      }
    });
  }, [
    isLoaded,
    mapInstance,
    coordinatesArray,
    plotVisible,
    plantationVisible,
    detailVisible,
  ]);

  return isLoaded ? (
    <GoogleMap
      center={center}
      zoom={16}
      mapContainerStyle={containerStyle}
      mapTypeId="hybrid"
      onLoad={(map) => setMapInstance(map)}
      onUnmount={() => setMapInstance(null)}
    />
  ) : null;
}
